<template>
  <div class="loan-repayments grid-container" :class="{ full: !$device.isMedium }">
    <div class="grid-x">
      <div class="cell">
        <h2 v-html="$t('heading', { name })" />
      </div>
    </div>
    <div class="grid-x">
      <div class="cell">
        <table>
          <thead>
            <tr>
              <th>{{ $t('table.headers.date') }}</th>
              <th>{{ $t('table.headers.expected') }}</th>
              <th>{{ $t('table.headers.actual') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="repayment in filteredRepayments" :key="$get(repayment, 'id')">
              <td>
                <time :datetime="repayment.dueAt|formatDate({ format: 'YYYY-MM-DD' })">
                  {{ repayment.dueAt|formatDate({ format: 'MMM YYYY' }) }}
                </time>
              </td>
              <td>{{ repayment.formattedExpected }}</td>
              <td>
                <figure v-if="repayment.repaidToUsersAt !== null">{{ repayment.formattedReceived }}</figure>
                <i18n v-else path="dateAvailable">
                  <template v-slot:date>
                    <time :datetime="repayment.dueToUsersAt|formatDate({ format: 'YYYY-MM-DD' })">
                      {{ repayment.dueToUsersAt|formatDate({ format: 'MMM YYYY'}) }}
                    </time>
                  </template>
                </i18n>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <div class="grid-x">
      <div class="cell">
        <button v-if="!showAll && loan.repayments.length > 6"
                class="button primary small no-chevron"
                @click.prevent="showAll = true">
          {{ $t('showAll') }}
        </button>
      </div>
    </div>

    <div class="grid-x">
      <div class="cell">
        <i18n path="exchangeRateWarning.content" tag="p">
          <template v-slot:link>
            <nuxt-link to="/info/help-centre/loans-and-lending">{{ $t('exchangeRateWarning.link.label') }}</nuxt-link>
          </template>
        </i18n>
      </div>
    </div>
  </div>
</template>

<script>
  import i18n from './locales'
  import { mapGetters } from 'vuex'
  import { getEntrepreneurName } from '~/modules/entrepreneurs/utils'
  import { date as formatDate } from '~/utils/filters'
  import BufferRepayment from '@loans/entities/frontend/buffer-repayment'
  import Repayment from '@loans/entities/frontend/repayment'
  import { orderBy } from 'lodash'

  export default {
    name: 'LoanDetailsRepayments',
    i18n,
    filters: {
      formatDate,
    },
    props: {
      bufferRepayment: {
        required: true,
        type: BufferRepayment,
      },
      repayments: {
        required: true,
        type: Array,
        validate: collection => collection.each(repayment => repayment instanceof Repayment),
      },
    },
    data () {
      return {
        showAll: false,
      }
    },
    computed: {
      ...mapGetters({
        loan: 'loans/current',
      }),
      name () {
        return getEntrepreneurName(this.loan.entrepreneur, {
          firstOnly: true,
          possessive: true,
        })
      },
      filteredRepayments () {
        return [
          this.bufferRepayment,
          ...this.orderedRepayments.slice(0, this.showAll ? undefined : 6),
        ]
      },
      orderedRepayments () {
        return orderBy(this.repayments, ['dueAt'], ['asc'])
      },
    },
  }
</script>

<style lang="scss" scoped>
  @import "~foundation-sites/scss/util/_unit";

  .loan-repayments {
    h2 {
      margin-top: 1rem;
      text-align: center;
    }

    table {
      font-size: rem-calc(14);
      margin-top: 2rem;
      margin-bottom: 2rem;

      th {
        width: 33%;
        text-transform: uppercase;
      }

      th, td {
        padding: rem-calc(15 10);
        text-align: center;
      }

      th:first-child,
      td:first-child {
        text-align: left;
      }
      th:last-child,
      td:last-child {
        text-align: right;
      }
    }
  }
</style>
